'use client';

import { Notepad, WarningCircle } from '@phosphor-icons/react';
import { Skeleton } from '@/components/ai-elements/skeleton';
import type { MemoryFactView } from './types';

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

interface FactsListProps {
  facts: MemoryFactView[];
  total: number;
  loading: boolean;
  serviceError: boolean;
}

export function FactsList({ facts, total, loading, serviceError }: FactsListProps) {
  return (
    <section className="rounded-2xl border border-border/70 bg-card/40 p-[18px]">
      <div className="flex items-center gap-2">
        <Notepad weight="duotone" className="size-4 text-violet-400" />
        <h2 className="text-sm font-extrabold tracking-tight">Facts</h2>
        {!loading && !serviceError && (
          <span className="text-[11px] text-muted-foreground">{total} total</span>
        )}
      </div>
      <p className="mt-0.5 text-[11px] text-muted-foreground">
        Everything the memory engine currently holds for this scope. Superseded facts stay
        visible with an “invalidated” badge — the engine never silently rewrites history.
      </p>

      <div className="mt-4 space-y-2">
        {loading ? (
          Array.from({ length: 4 }, (_, i) => <Skeleton key={i} className="h-14 w-full rounded-xl" />)
        ) : serviceError ? (
          <div className="flex items-center gap-2 rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-[12px] text-amber-600 dark:text-amber-400">
            <WarningCircle weight="duotone" className="size-4 shrink-0" />
            The memory service could not be reached. Facts are temporarily unavailable.
          </div>
        ) : facts.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border/60 px-4 py-8 text-center">
            <p className="text-sm font-medium">No facts in this scope</p>
            <p className="mx-auto mt-1 max-w-md text-[12px] leading-snug text-muted-foreground">
              As you chat, durable facts are extracted and land here. Nothing is stored from
              tool results or documents — only what you and the assistant say.
            </p>
          </div>
        ) : (
          facts.map((fact) => (
            <div
              key={fact.id}
              className={`rounded-xl border px-4 py-3 ${
                fact.invalidAt
                  ? 'border-border/50 bg-muted/20 opacity-70'
                  : 'border-border/60 bg-background/40'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <p
                  className={`text-sm leading-snug ${
                    fact.invalidAt ? 'text-muted-foreground line-through decoration-muted-foreground/40' : 'text-foreground/90'
                  }`}
                >
                  {fact.content}
                </p>
                {fact.invalidAt && (
                  <span className="mt-0.5 shrink-0 rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400">
                    invalidated
                  </span>
                )}
              </div>
              <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10.5px] text-muted-foreground/70">
                <span>valid {formatDate(fact.validAt)}</span>
                {fact.invalidAt && <span>→ invalid {formatDate(fact.invalidAt)}</span>}
                <span>· created {formatDate(fact.createdAt)}</span>
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  );
}
